<template>
  <div>
    <button :disabled="disabled" @click="newListing()"
      class="bg-turquoise-500/10 rounded-full border border-turquoise-500/50 btn xl:py-1.5 xl:px-6 font-normal min-h-0 h-auto text-turquoise-500 xl:text-lg p-2 px-3 sm:p-2 sm:px-8 sm:text-xl">
      <span class="hidden xl:block">New Listing</span>
      <span class="xl:hidden">New</span>
    </button>

    <HeadlessTransitionRoot as="template" :show="open">
      <HeadlessDialog as="div" class="relative z-50" @close="open = false">
        <HeadlessTransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
          leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </HeadlessTransitionChild>

        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <HeadlessTransitionChild as="template" enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <HeadlessDialogPanel
                class="relative transform overflow-hidden rounded-lg bg-woodsmoke-850 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm">
                <div class="w-full flex justify-between items-center border-b border-b-white/10 p-4">
                  <div class="flex justify-start items-center space-x-2">
                    <h1 class="text-lg text-turquoise-500">New Listing</h1>
                    <OTCGuide start="create">
                      <button
                        class="w-6 h-6 bg-turquoise-500/30 flex justify-center items-center rounded-full btn btn-circle min-h-0 m-0 p-0 border-none">
                        <Icon class="text-turquoise-500 text-xl" name="bi:question"></Icon>
                      </button>
                    </OTCGuide>
                  </div>

                  <button @click="closeModal()" class="btn btn-sm bg-white/5 capitalize text-red-500">close</button>
                </div>

                <div class="w-full grid grid-cols-3 text-center text-sm border-b border-b-white/10">
                  <div :class="['w-full text-left flex justify-start items-center p-1 px-2 space-x-2']">
                    <div class="w-5 h-5 text-xs rounded-full bg-white/10 flex justify-center items-center">1</div>
                    <p>Pool</p>
                  </div>

                  <div
                    class="w-full text-left flex justify-start items-center p-1 px-2 space-x-2 border-x border-x-white/10">
                    <div class="w-5 h-5 text-xs rounded-full bg-white/10 flex justify-center items-center">2</div>
                    <p>Price</p>
                  </div>

                  <div class="w-full text-left flex justify-start items-center p-1 px-2 space-x-2">
                    <div class="w-5 h-5 text-xs rounded-full bg-white/10 flex justify-center items-center">3</div>
                    <p>Confirmation</p>
                  </div>

                  <div class="w-full h-1 border-t border-t-white/10 col-span-full">
                    <div
                      :class="['h-full bg-turquoise-500 animate-pulse duration-300', page == 'pool' ? 'w-0' : '', page == 'price' ? 'w-[33.33%]' : '', page == 'confirm' ? 'w-[66.66%]' : '']">
                    </div>
                  </div>
                </div>

                <section v-if="page == 'pool'" class="p-4">
                  <h1 class="text-white text-lg mb-2">Select Pool</h1>

                  <article class="w-full h-60 border border-white/10 bg-white/5 rounded-md overflow-y-auto">
                    <div @click="selectPool(_pool)" v-for="_pool in pools"
                      class="w-full grid grid-cols-8 p-2 border-b border-b-white/10 odd:bg-white/5 hover:bg-white/10 justify-center items-center gap-2 cursor-pointer">
                      <div class="w-full aspect-square border border-white/50 rounded-md">
                        <img :src="_pool.project.logo" alt="" class="w-full h-full object-cover rounded-md" />
                      </div>

                      <div class="col-span-5">
                        <p class="text-sm text-white">{{ _pool.project.name }} - {{ _pool.pool.name }}</p>
                        <p class="text-sm">Allocation: {{ Number(_pool.allocation.value).toFixed(2) }} {{
                          _pool.allocation.currency }}</p>
                      </div>

                      <div class="w-full flex justify-end items-center col-span-2">
                        <button class="btn btn-xs w-full bg-white/5 border-none text-turquoise-600">Select</button>
                      </div>
                    </div>
                  </article>

                  <div class="mt-8">
                    <p class="mb-2 text-xs uppercase tracking-widest text-white">You've Selected</p>
                    <div class="w-full grid grid-cols-5">
                      <div class="col-span-4 flex justify-start items-center">
                        <template v-if="selectedPool">
                          <div class="h-10 w-10 border border-white/10 rounded-md">
                            <img :src="selectedPool.project.logo" alt="" class="w-10 h-10 object-cover rounded-md" />
                          </div>

                          <div class="pl-2">
                            <p class="text-woodsmoke-50 text-sm font-bold">{{ selectedPool.project.name }} - {{
                              selectedPool.pool.name }}</p>
                            <p class="text-woodsmoke-50 text-sm">Allocation: {{
                              Number(selectedPool.allocation.value).toFixed(2) }} {{ selectedPool.allocation.currency }}
                            </p>
                          </div>
                        </template>
                        <template v-else>
                          <p class="text-woodsmoke-50 text-sm col-span-4">No Pool Selected</p>
                        </template>
                      </div>

                      <button :disabled="!selectedPool" @click="nextPage()"
                        class="btn bg-turquoise-500 text-turquoise-1000 w-full min-h-0 h-auto p-3 m-0 hover:text-turquoise-500">Next</button>
                    </div>
                  </div>
                </section>

                <section v-if="page == 'price'" class="p-4">
                  <h1 class="text-white text-lg mb-8">Configure Listing Price</h1>

                  <article class="w-full">
                    <div class="w-full flex justify-between items-center bg-white/5 p-2 rounded-md mb-8">
                      <p class="text-sm">Available Allocation</p>
                      <p>{{ Number(selectedPool.allocation.value).toFixed(2) }} {{ selectedPool.allocation.currency }}</p>
                    </div>

                    <div class="space-y-2">
                      <div class="w-full grid grid-cols-7 justify-center items-center">
                        <div class="col-span-3 text-sm text-woodsmoke-50">
                          <p>Allocation to Sell</p>
                        </div>

                        <div class="col-span-4 flex justify-end items-center space-x-2">
                          <input v-model="selectedAllocationToSell" type="number" placeholder="Enter Allocation to Sell"
                            class="text-right input input-bordered w-full input-sm" />
                          <p>{{ selectedPool.allocation.currency }}</p>
                        </div>
                      </div>

                      <div class="w-full grid grid-cols-7 justify-center items-center">
                        <div class="col-span-3 text-sm text-woodsmoke-50">
                          <p>Listing Price</p>
                        </div>

                        <div class="col-span-4 flex justify-end items-center space-x-2">
                          <input @input="updatePrice({ source: 'listing_price' })" v-model="selectedListingPrice"

                            type="number" placeholder="Enter Listing Price "
                            class="text-right input input-bordered w-full input-sm" />
                          <p>{{ selectedPool.allocation.currency }}</p>
                        </div>
                      </div>

                      <div class="w-full grid grid-cols-7 justify-center items-center">
                        <div class="col-span-3 text-sm text-woodsmoke-50">
                          <p>Markup Amount</p>
                        </div>

                        <div class="col-span-4 text-right">
                          <!-- <input @input="updatePrice({source:'markup_amount'})" v-model="selectedMarkupAmount"  type="text" placeholder="Enter Markup Amount" class="input input-bordered w-full input-sm" /> -->
                          {{ selectedMarkupAmount }} {{ selectedPool.allocation.currency }}
                        </div>
                      </div>

                      <div class="w-full grid grid-cols-7 justify-center items-center">
                        <div class="col-span-3 text-sm text-woodsmoke-50">
                          <p>Markup %</p>
                        </div>

                        <div class="col-span-4 text-right">
                          <!-- <URange @input="updatePrice({source:'markup_percent'})" size="sm" class="w-full" color="primary" v-model="selectedMarkupPercent" /> -->
                          <!-- <input @input="updatePrice({source:'markup_percent'})" v-model="selectedMarkupPercent" type="text" placeholder="" class="input input-bordered input-sm w-14" /> -->
                          <!-- <div>%</div> -->
                          {{ selectedMarkupPercent }}%
                        </div>
                      </div>
                    </div>
                  </article>

                  <div class="w-full mt-8 flex justify-between items-center">
                    <div>
                      <p class="text-sm">Listing Price</p>
                      <p class="text-lg text-white">{{ selectedListingPrice }} {{ selectedPool.allocation.currency }}</p>
                    </div>

                    <button :disabled="!shouldEnableNextButton()" @click="nextPage()"
                      class="btn bg-turquoise-500 text-turquoise-1000 min-h-0 h-auto p-3 m-0 hover:text-turquoise-500">Next</button>
                  </div>

                  <div class="w-full flex justify-start items-center">
                    <button @click="prevPage()"
                      class="btn btn-sm btn-link text-red-500 font-normal text-left p-0 m-0 mt-2">Back to Previous
                      Step</button>
                  </div>

                  <!-- <p class="w-full text-sm text-woodsmoke-400 text-center pt-6">Proceed to summary, simulation and confirmation</p> -->
                </section>

                <section v-if="page == 'confirm'" class="p-4">
                  <div class="mb-12">
                    <h1 class="text-white text-lg">Confirmation & Simulation</h1>
                    <p class="text-sm text-woodsmoke-50">Use our calculator to simulate your final profit</p>
                  </div>

                  <div class="w-full flex justify-between items-center mt-4 px-1 text-sm">
                    <p class="text-woodsmoke-300">Allocation to Sell</p>
                    <p class="text-white">{{ selectedAllocationToSell }} {{ selectedPool.allocation.currency }}</p>
                  </div>

                  <div class="w-full flex justify-between items-center px-1 text-sm">
                    <p class="text-woodsmoke-300">Markup</p>
                    <p class="text-white">{{ selectedMarkupAmount }} {{ selectedPool.allocation.currency }} ( {{
                      selectedMarkupPercent }}%)</p>
                  </div>

                  <div class="w-full flex justify-between items-center px-1 text-sm">
                    <p class="text-woodsmoke-300">Listing Price</p>
                    <p class="text-white">{{ selectedListingPrice }} {{ selectedPool.allocation.currency }}</p>
                  </div>

                  <div v-if="isPrivate" class="w-full flex justify-between items-center px-1 text-sm">
                    <p class="text-woodsmoke-300">Private Fee</p>
                    <p class="">
                      <span class="mr-2">{{ selectedListingPrice }} {{ selectedPool.allocation.currency }} x 3% =</span>
                      <span class="text-white">{{ (selectedListingPrice * 0.03).toFixed(2) }} {{ selectedPool.allocation.currency
                      }}</span>
                    </p>
                  </div>

                  <OTCSimulationDiagramSeller :price="selectedListingPrice" :allocation="selectedAllocationToSell"
                    :markup="selectedMarkupAmount"></OTCSimulationDiagramSeller>

                  <div class="mt-8">
                    <p class="text-sm text-white">By proceeding, you understand that:</p>
                    <p class="text-sm">1. The final profit is subject to change based on the project's contribution
                      status.</p>
                    <p class="text-sm">2. You may receive the amount less than the listing price</p>
                    <p class="text-sm">3. Your profit is subject to a 5% platform fee</p>
                    <p v-if="isPrivate" class="text-sm">4. Your listing price is subjected to 3% private fee, and your
                      profit is calculated exclusive of the private fee.</p>
                  </div>

                  <div class="w-full mt-8 flex justify-between items-center">
                    <div>
                      <p class="text-sm">Listing Price</p>
                      <p class="text-lg text-white">
                        <span v-if="isPrivate">{{ selectedListingPrice * 1.03 }} {{ selectedPool.allocation.currency
                        }}</span>
                        <span v-if="!isPrivate">{{ selectedListingPrice }} {{ selectedPool.allocation.currency }}</span>
                      </p>
                    </div>

                    <button @click="confirmListing()"
                      class="btn bg-turquoise-500 text-turquoise-1000 min-h-0 h-auto p-3 m-0 hover:text-turquoise-500">Confirm
                      & List</button>
                  </div>

                  <div class="w-full flex justify-start items-center">
                    <button @click="prevPage()"
                      class="btn btn-sm btn-link text-red-500 font-normal text-left p-0 m-0 mt-2">Back to Previous
                      Step</button>
                  </div>
                </section>
              </HeadlessDialogPanel>
            </HeadlessTransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </HeadlessTransitionRoot>
  </div>
</template>

<script setup>
import { useUserStore } from "~/stores/user.js";

const props = defineProps(["disabled", "pools"]);
const { disabled, pools } = toRefs(props);

const emit = defineEmits(["listed"]);

const userStore = useUserStore();

//const pools = ref([]);

//async function renderAllocation() {
//  //let _account = getAccount(config);
//
//  if (userStore.authenticated) {
//    const result = await $fetch("/api/otc/allocations", {
//      headers: {
//        Authorization: `Bearer ${userStore.accessToken}`,
//      },
//    });
//    pools.value = result.allocations;
//  }
//}

//const unsub = userStore.$subscribe((state) => {
//  renderAllocation();
//});
//
//onMounted(() => {
//  renderAllocation();
//});
//
//onUnmounted(() => {
//  unsub();
//});

const open = ref(false);

const newListing = () => {
  open.value = true;
};

const page = ref("pool");

const closeModal = () => {
  open.value = false;
  page.value = "pool";
};

const toast = useToast();
const _toast = (param) => {
  toast.add({
    ...param,
    ui: {
      strategy: "override",
      icon: {
        base: "flex-shrink-0 w-5 h-5 mt-0.5",
        color: "text-{color}-500 dark:text-{color}-400",
      },
      background: "bg-woodsmoke-800/50 backdrop-blur-md",
      container: "border border-turquoise-500/20 relative overflow-hidden",
      title: "text-turquoise-500 text-base",
      description: "text-white text-sm",
    },
  });
};

const confirmListing = async () => {

  userStore.loading = true;
  open.value = false;
  page.value = "pool";

  return $fetch("/api/otc/list", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${$accessToken.value}`,
    },
    body: JSON.stringify({
      allocation_id: selectedPool.value.allocation.id,
      allocation_amount: selectedAllocationToSell.value,
      investor_reward: selectedMarkupAmount.value,
    }),
  })
    .then(() => {
      //renderAllocation()
      emit("listed");
      userStore.loading = false
    })
    .catch((err) => {
      _toast({
        id: "listed",
        title: "Fail",
        description: err.data.message,
        timeout: 5000,
      });
      userStore.loading = false
    });
};

const nextPage = () => {
  if (page.value == "pool") {
    page.value = "price";
    selectedAllocationToSell.value = Number(selectedPool.value.allocation.value.toFixed(2));
  } else if (page.value == "price") {
    page.value = "confirm";
  } else if (page.value == "confirm") {
    open.value = false;
  }
};

const prevPage = () => {
  if (page.value == "price") {
    page.value = "pool";
  } else if (page.value == "confirm") {
    page.value = "price";
  }
};

const isPrivate = computed(() => {
  return selectedPool.value.pool.is_private;
});

// form data

const selectedPool = ref(null);
const selectPool = (_pool) => {
  selectedPool.value = _pool;
};

const selectedAllocationToSell = ref(null);
const selectedListingPrice = ref(null);
const selectedMarkupAmount = ref(0);
const selectedMarkupPercent = ref(0);

const shouldEnableNextButton = () => {
  return selectedAllocationToSell.value 
  && selectedAllocationToSell.value > 0 
  && (selectedPool.value.allocation.value >= selectedAllocationToSell.value)
  && selectedListingPrice.value > selectedAllocationToSell.value
}

const updatePrice = (args) => {
  const calculatePrice = (_args) => {
    selectedMarkupAmount.value = parseFloat(Number(selectedListingPrice.value - selectedAllocationToSell.value).toFixed(2));
    selectedMarkupPercent.value = parseFloat(Number((selectedMarkupAmount.value / selectedAllocationToSell.value) * 100).toFixed(2));

    selectedMarkupAmount.value = selectedMarkupAmount.value < 0 ? 0 : selectedMarkupAmount.value;
    selectedMarkupPercent.value = selectedMarkupPercent.value < 0 ? 0 : selectedMarkupPercent.value;
  };

  if (!selectedAllocationToSell.value) return;

  calculatePrice(args);
};
</script>

<style></style>
